import { Folder, Inbox, Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { composerBus } from "@/lib/composerBus";
import { ArchivedRow } from "./ArchivedRow";
import { useArchivePanel } from "./useArchivePanel";
import { ArchiveFolderRail } from "./ArchiveFolderRail";
import { ArchiveDialogs } from "./ArchiveDialogs";

type Props = {
  workspaceId: string;
};

export const ArchivePanel = ({ workspaceId }: Props) => {
  const a = useArchivePanel(workspaceId);

  const allVisibleSelected = a.visiblePieces.length > 0 && a.visiblePieces.every((p) => a.selected.has(p.id));

  return (
    <div className="flex h-full min-h-0">
      <ArchiveFolderRail
        folders={a.folders}
        counts={a.counts}
        activeFolder={a.activeFolder}
        setActiveFolder={a.setActiveFolder}
        newFolderName={a.newFolderName}
        setNewFolderName={a.setNewFolderName}
        createPending={a.createMut.isPending}
        onCreate={(name) => a.createMut.mutate(name)}
        dragFolderId={a.dragFolderId}
        setDragFolderId={a.setDragFolderId}
        onDropOnFolder={a.handleDropOnFolder}
        onFolderReorderDrop={a.handleFolderReorderDrop}
        onRequestRename={(f) => {
          a.setRenaming(f);
          a.setRenameValue(f.name);
        }}
        onRequestDelete={(f) => a.setDeleting(f)}
      />

      <div className="flex min-w-0 flex-1 flex-col">
        <div className="space-y-2 border-b border-border/60 px-3 py-2">
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs font-medium">
              {a.folderTitle}
              <span className="ml-1.5 text-muted-foreground">({a.visiblePieces.length})</span>
            </p>
            <div className="relative w-56">
              <Search className="absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={a.search}
                onChange={(e) => a.setSearch(e.target.value)}
                placeholder="Search title or content…"
                className="h-7 pl-6 pr-6 text-xs"
              />
              {a.search && (
                <button
                  type="button"
                  onClick={() => a.setSearch("")}
                  className="absolute right-1 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground hover:bg-muted"
                  aria-label="Clear search"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
          </div>
          {a.visiblePieces.length > 0 && (
            <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
              <Checkbox
                checked={allVisibleSelected}
                onCheckedChange={(c) => {
                  if (c) a.setSelected(new Set(a.visiblePieces.map((p) => p.id)));
                  else a.setSelected(new Set());
                }}
              />
              {a.selected.size > 0 ? (
                <>
                  <span className="font-medium text-foreground">{a.selected.size} selected</span>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button size="sm" variant="outline" className="h-6 text-[11px]">
                        Move to folder…
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start">
                      <DropdownMenuItem
                        onClick={() => a.bulkAssignMut.mutate({ ids: Array.from(a.selected), folderId: null })}
                      >
                        <Inbox className="mr-2 h-3.5 w-3.5" /> Unfiled
                      </DropdownMenuItem>
                      {a.folders.length > 0 && <DropdownMenuSeparator />}
                      {a.folders.map((f) => (
                        <DropdownMenuItem
                          key={f.id}
                          onClick={() => a.bulkAssignMut.mutate({ ids: Array.from(a.selected), folderId: f.id })}
                        >
                          <Folder className="mr-2 h-3.5 w-3.5" /> {f.name}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-6 text-[11px]"
                    onClick={() => a.setSelected(new Set())}
                  >
                    Clear
                  </Button>
                </>
              ) : (
                <span>Select pieces to move them in bulk · drag a folder to reorder</span>
              )}
            </div>
          )}
        </div>
        <ScrollArea className="flex-1">
          {a.piecesQuery.isLoading ? (
            <div className="p-6 text-center text-xs text-muted-foreground">Loading…</div>
          ) : a.visiblePieces.length === 0 ? (
            <div className="p-6 text-center text-xs text-muted-foreground">
              {a.debouncedSearch
                ? `No pieces match "${a.debouncedSearch}".`
                : a.counts.total === 0
                  ? "Nothing published yet. Pieces appear here after their first send/export."
                  : "No pieces in this folder. Drag one in from another folder."}
            </div>
          ) : (
            <div className="space-y-1 p-2">
              {a.visiblePieces.map((p) => (
                <ArchivedRow
                  key={p.id}
                  piece={p}
                  workspaceId={workspaceId}
                  draggable
                  selected={a.selected.has(p.id)}
                  onToggleSelect={() => a.toggleSelect(p.id)}
                  onDragStart={() => a.setDragPieceId(p.id)}
                  onDragEnd={() => a.setDragPieceId(null)}
                  onClick={() => composerBus.emit("piece:open", { pieceId: p.id, label: p.title || undefined })}
                />
              ))}
            </div>
          )}
        </ScrollArea>
      </div>

      <ArchiveDialogs
        renaming={a.renaming}
        setRenaming={a.setRenaming}
        renameValue={a.renameValue}
        setRenameValue={a.setRenameValue}
        onRename={(id, name) => a.renameMut.mutate({ id, name })}
        deleting={a.deleting}
        setDeleting={a.setDeleting}
        onDelete={(id) => a.deleteMut.mutate(id)}
      />
    </div>
  );
};

export default ArchivePanel;
